import { useState, useEffect, useRef } from "react";
import { Toaster, toast } from "react-hot-toast";
import { Spinner } from "reactstrap";

import {
  formatCLP,
  validRut,
  validateEmail,
  validatePhone,
  formatRutInput,
  cleanRut,
} from "../../../helpers/functions";
import PublicCatalogAPI from "../../../services/api/public-catalog.api";
import PublicPaymentAPI from "../../../services/api/public-payment.api";
import { ButtonLoading } from "../../../components/buttons/button-loading";
import ProductCard from "../../private/checkout/components/product-card";
import InvoiceSummary from "../../private/checkout/components/invoice-summary";
import TermsModal from "../../private/checkout/components/terms-modal";
import CaptchaWidget from "../../../components/captcha/captcha-widget";
import PublicLayout from "../../../layout/public-layout";
import { CAPTCHA_ENABLED } from "../../../helpers/constants";

const VALIDATION_MESSAGES = {
  companyName: "Ingresa el nombre de tu empresa",
  rut: "Ingresa un RUT válido",
  email: "Ingresa un email válido",
  phone: "Ingresa un teléfono válido (ej: 912345678)",
};

const initialTouched = { companyName: false, rut: false, email: false, phone: false };

const PublicCheckout = () => {
  const [catalog, setCatalog] = useState([]);
  const [baseUnitPrice, setBaseUnitPrice] = useState(null);
  const [fetching, setFetching] = useState(true);

  const [companyName, setCompanyName] = useState("");
  const [rut, setRut] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [touched, setTouched] = useState(initialTouched);
  const [submitted, setSubmitted] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    setFetching(true);
    PublicCatalogAPI.getCatalog()
      .then((res) => {
        const data = res?.data ?? [];
        setCatalog(data);
        const individual = data.find((p) => p.testsQuantity === 1);
        setBaseUnitPrice(individual?.totalPrice ?? null);
        const recommended = data.find((p) => p.isRecommended);
        setSelectedPlan(recommended ?? null);
      })
      .catch(() => {
        toast.error("No pudimos cargar los planes disponibles");
      })
      .finally(() => setFetching(false));
  }, []);

  const isCompanyValid = companyName.trim().length > 0;
  const isRutValid = validRut(rut.trim());
  const isEmailValid = email.trim().length > 0 && validateEmail(email.trim()) === false;
  const isPhoneValid = phone.trim().length > 0 && validatePhone(phone.trim());
  const isFormValid = isCompanyValid && isRutValid && isEmailValid && isPhoneValid;
  const canSubmit =
    isFormValid && !!selectedPlan && termsAccepted && (!CAPTCHA_ENABLED || !!captchaToken);

  const fieldValidity = {
    companyName: isCompanyValid,
    rut: isRutValid,
    email: isEmailValid,
    phone: isPhoneValid,
  };

  const showError = (field) => (touched[field] || submitted) && !fieldValidity[field];

  const handleFieldChange = (field, value) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === "companyName") setCompanyName(value);
    if (field === "rut") setRut(formatRutInput(value));
    if (field === "email") setEmail(value);
    if (field === "phone") setPhone(value);
  };

  const handleSelectPlan = (plan) => setSelectedPlan(plan);

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const response = await PublicPaymentAPI.createPublicPaymentIntention({
        companyName: companyName.trim(),
        rut: cleanRut(rut.trim()),
        email: email.trim(),
        phone: phone.trim(),
        catalogId: selectedPlan.id,
        captchaToken,
      });
      if (response?.data?.redirectUrl) {
        window.location.href = response.data.redirectUrl;
      } else {
        toast.error("Ocurrió un error, intenta nuevamente");
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        toast.error("No pudimos verificar que eres humano. Intenta nuevamente.");
      } else if (error?.response?.status === 422 && typeof error.response.data === "string" && error.response.data) {
        toast.error(error.response.data);
      } else {
        toast.error("Ocurrió un error, intenta nuevamente");
      }
      // Los tokens de captcha son de un solo uso: hay que reiniciar el widget
      // ante cualquier fallo, no solo ante el 403. Sin esto, corregir un RUT
      // duplicado tras un 422 fallaría siempre con un 403.
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
    <div className="gm-clients-details-container">
      <Toaster />

      {fetching ? (
        <div className="gm-checkout-loading">
          <Spinner size="sm" />
          &nbsp; Cargando...
        </div>
      ) : (
        <>
          <div className="gm-folder-container" style={{ marginTop: 20 }}>
            <div className="gm-folder-title">Compra tu plan</div>
            <form
              className="gm-form"
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(true);
              }}
            >
              <div className="gm-form-container">
                <div className="gm-form-section">
                  <div className="gm-form-section-title">Datos de la empresa</div>
                  <div className="gm-form-group">
                    <div className="gm-input-group">
                      <label htmlFor="companyName">Empresa</label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={companyName}
                        onChange={(e) => handleFieldChange("companyName", e.target.value)}
                        aria-invalid={showError("companyName")}
                        aria-describedby={showError("companyName") ? "companyName-error" : undefined}
                        required
                      />
                      {showError("companyName") && (
                        <span id="companyName-error" className="gm-input-error">{VALIDATION_MESSAGES.companyName}</span>
                      )}
                    </div>
                    <div className="gm-input-group">
                      <label htmlFor="rut">RUT</label>
                      <input
                        type="text"
                        id="rut"
                        name="rut"
                        value={rut}
                        onChange={(e) => handleFieldChange("rut", e.target.value)}
                        aria-invalid={showError("rut")}
                        aria-describedby={showError("rut") ? "rut-error" : undefined}
                        maxLength={13}
                        required
                      />
                      {showError("rut") && (
                        <span id="rut-error" className="gm-input-error">{VALIDATION_MESSAGES.rut}</span>
                      )}
                    </div>
                  </div>
                  <div className="gm-form-group">
                    <div className="gm-input-group">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={(e) => handleFieldChange("email", e.target.value)}
                        aria-invalid={showError("email")}
                        aria-describedby={showError("email") ? "email-error" : undefined}
                        required
                      />
                      {showError("email") && (
                        <span id="email-error" className="gm-input-error">{VALIDATION_MESSAGES.email}</span>
                      )}
                    </div>
                    <div className="gm-input-group">
                      <label htmlFor="phone">Teléfono</label>
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        value={phone}
                        onChange={(e) => handleFieldChange("phone", e.target.value)}
                        aria-invalid={showError("phone")}
                        aria-describedby={showError("phone") ? "phone-error" : undefined}
                        required
                      />
                      {showError("phone") && (
                        <span id="phone-error" className="gm-input-error">{VALIDATION_MESSAGES.phone}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="gm-folder-container" style={{ marginTop: 20 }}>
            <div className="gm-folder-title">Elige tu plan</div>
            <div className="gm-folder-content-container">
              <div className="gm-plan-cards-container">
                {catalog.map((plan) => {
                  const isSelected = selectedPlan?.id === plan.id;
                  return (
                    <div
                      key={plan.id}
                      className={`gm-plan-card${isSelected ? " gm-plan-card--recommended" : ""}`}
                    >
                      {plan.isRecommended && <div className="gm-plan-badge">Más Popular</div>}
                      <div className="gm-plan-name">{plan.name}</div>
                      <div className="gm-plan-tests-label">
                        {plan.testsQuantity} {plan.testsQuantity === 1 ? "test" : "tests"}/año
                      </div>
                      <div className="gm-plan-price">{formatCLP(plan.totalPrice)}</div>
                      <div className="gm-plan-price-period">al año</div>
                      <div className="gm-plan-unit-price">{formatCLP(plan.unitPrice)} por test</div>
                      {plan.discountPercentage > 0 && (
                        <div className="gm-plan-discount">{plan.discountPercentage}% ahorro</div>
                      )}
                      <ButtonLoading
                        type="button"
                        className={`gm-plan-buy-btn${isSelected ? " gm-plan-buy-btn--recommended" : ""}`}
                        onClick={() => handleSelectPlan(plan)}
                        buttonLabel={isSelected ? "Seleccionado" : "Seleccionar"}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {selectedPlan && (
            <div className="gm-folder-container" style={{ marginTop: 20 }}>
              <div className="gm-folder-content-container" style={{ gap: 0 }}>
                <div className="gm-checkout-grid">
                  <ProductCard plan={selectedPlan} />
                  <InvoiceSummary
                    plan={selectedPlan}
                    baseUnitPrice={baseUnitPrice}
                    termsAccepted={termsAccepted}
                    onTermsChange={setTermsAccepted}
                    onOpenTerms={() => setTermsOpen(true)}
                    onConfirm={handleConfirm}
                    loading={loading}
                    disableConfirm={!canSubmit}
                    captchaSlot={
                      <CaptchaWidget
                        ref={captchaRef}
                        onToken={setCaptchaToken}
                        onExpire={() => setCaptchaToken(null)}
                        onError={() => setCaptchaToken(null)}
                      />
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <TermsModal
            open={termsOpen}
            toggle={() => setTermsOpen((o) => !o)}
            onAccept={() => setTermsAccepted(true)}
          />
        </>
      )}
    </div>
    </PublicLayout>
  );
};

export default PublicCheckout;
